// src/components/Registration.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db, auth } from "../firebase"; // Make sure this path is correct
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where,
  getDocs,
  doc,        // ← ADD THIS
  getDoc      // ← ADD THIS
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { tradingApi } from "../services/api";
import EmailNotificationService from "../services/emailNotifications";

function InvestRegistration() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "", // This will be auto-filled
    phoneNumber: "",
    accountNumber: "",
    brokerName: "",
    initialCapital: "",
    tradingExperience: "Beginner",
    riskTolerance: "Conservative",
    lotMultiplier: "0.1",
    maxTrades: "5",
    preferredSymbols: [],
    expectations: [],
    subscriptionPlan: "trial",
    paymentMethod: "crypto",
  });

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [isRegistering, setIsRegistering] = useState(false);
  const navigate = useNavigate();

  // Get authenticated user's email on component mount
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const email = currentUser.email;
      setUserEmail(email);
      setFormData(prev => ({ ...prev, email: email }));
    } else {
      // If somehow no user, redirect to auth
      navigate('/auth');
    }
  }, [navigate]);

  const questions = [
    {
      text: `Welcome to KS Quantitative Institutional Portfolios, ${userEmail ? userEmail.split('@')[0] : 'Trader'}! Let's start with your full name`,
      name: "name",
      type: "text",
      required: true,
      icon: "👤",
    },
    {
      text: "Confirm your email address",
      name: "email",
      type: "email-readonly", // Custom type for read-only email
      required: true,
      icon: "📧",
      note: "This email is linked to your account and cannot be changed"
    },
    {
      text: "Your phone number for important updates",
      name: "phoneNumber",
      type: "tel",
      required: true,
      icon: "📱",
    },
    {
      text: "Institutional Brokerage Account ID",
      name: "accountNumber",
      type: "text",
      required: true,
      icon: "🔢",
      placeholder: "Enter Account ID or API Identifier",
    },
    {
      text: "Which Prime Broker or Exchange do you use?",
      name: "brokerName",
      type: "select",
      required: true,
      icon: "🏢",
      options: [
        { value: "LMAX", label: "LMAX Institutional" },
        { value: "InteractiveBrokers", label: "Interactive Brokers Pro" },
        { value: "Saxo", label: "Saxo Institutional" },
        { value: "IG", label: "IG Prime" },
        { value: "BinanceVIP", label: "Binance Institutional" },
        { value: "PepperstonePro", label: "Pepperstone Pro" },
        { value: "ICMarkets", label: "IC Markets Raw" },
        { value: "Other", label: "Other Prime Broker" },
      ],
    },
    {
      text: "What's your initial trading capital?",
      name: "initialCapital",
      type: "select",
      required: true,
      icon: "💰",
      options: [
        { value: "10000-50000", label: "$10,000 - $50,000" },
        { value: "50000-250000", label: "$50,000 - $250,000" },
        { value: "250000-1000000", label: "$250,000 - $1,000,000" },
        { value: "1000000-5000000", label: "$1,000,000 - $5,000,000" },
        { value: "5000000+", label: "$5,000,000+" },
      ],
    },
    {
      text: "Your trading experience level?",
      name: "tradingExperience",
      type: "select",
      required: true,
      icon: "📈",
      options: [
        { value: "Beginner", label: "Beginner (0-1 year)" },
        { value: "Intermediate", label: "Intermediate (1-3 years)" },
        { value: "Advanced", label: "Advanced (3-5 years)" },
        { value: "Expert", label: "Expert (5+ years)" },
      ],
    },
    {
      text: "What's your risk tolerance?",
      name: "riskTolerance",
      type: "select",
      required: true,
      icon: "⚖️",
      options: [
        { value: "Conservative", label: "Conservative (Low Risk)" },
        { value: "Moderate", label: "Moderate (Medium Risk)" },
        { value: "Aggressive", label: "Aggressive (High Risk)" },
      ],
    },
    {
      text: "Algorithmic Trade Allocation Multiplier",
      name: "lotMultiplier",
      type: "select",
      required: true,
      icon: "📊",
      options: [
        { value: "0.01", label: "0.01x (Minimum Risk Allocation)" },
        { value: "0.1", label: "0.1x (Conservative Allocation)" },
        { value: "0.5", label: "0.5x (Half Allocation)" },
        { value: "1.0", label: "1.0x (Standard Allocation)" },
      ],
    },
    {
      text: "Maximum concurrent trades",
      name: "maxTrades",
      type: "select",
      required: true,
      icon: "🎯",
      options: [
        { value: "3", label: "3 trades max" },
        { value: "5", label: "5 trades max" },
        { value: "10", label: "10 trades max" },
        { value: "15", label: "15 trades max" },
      ],
    },
    {
      text: "Which asset classes or instruments interest you?",
      name: "preferredSymbols",
      type: "checkbox",
      required: true,
      icon: "🌐",
      options: [
        { value: "XAUUSD", label: "Gold (XAUUSD)" },
        { value: "US100", label: "NASDAQ (US100)" },
        { value: "USOIL", label: "Crude Oil (WTI)" },
        { value: "EURUSD", label: "EUR/USD" },
        { value: "GBPUSD", label: "GBP/USD" },
        { value: "USDJPY", label: "USD/JPY" },
        { value: "BTCUSD", label: "Bitcoin (BTC/USD)" },
        { value: "ETHUSD", label: "Ethereum (ETH/USD)" },
        { value: "FX_MAJORS", label: "Other FX Majors" },
        { value: "OTHERS", label: "Other Instruments" },
        { value: "None", label: "No preference" }
      ],
    },
    {
      text: "What are your expectations from this quantitative portfolio?",
      name: "expectations",
      type: "checkbox",
      required: true,
      icon: "🎯",
      options: [
        { value: "Passive Income", label: "Generate algorithmic alpha" },
        { value: "Learn Trading", label: "Access proprietary trading strategies" },
        { value: "Diversify Portfolio", label: "Diversify alternative asset portfolio" },
        { value: "Risk Management", label: "Strict institutional risk management" },
        { value: "Consistent Profits", label: "Consistent quantitative returns" },
      ],
    },
    {
      text: "Select your fee structure (High Water Mark)",
      name: "subscriptionPlan",
      type: "select",
      required: true,
      icon: "💎",
      options: [
        { value: "performance_20", label: "20% Profit Share (Accounts > $250,000)" },
        { value: "performance_25", label: "25% Profit Share (Accounts $50,000 - $250,000)" },
        { value: "performance_30", label: "30% Profit Share (Accounts < $50,000)" },
      ],
    },
  ];

  const validateField = (name, value, type) => {
    switch (type) {
      case "text":
        if (!value || !value.trim()) {
          return "This field is required";
        }
        if (name === "accountNumber" && !/^\d{4,12}$/.test(value)) {
          return "Account number should be 4-12 digits";
        }
        return "";

      case "email-readonly":
        // Email is pre-filled and validated, always valid
        return "";

      case "tel":
        if (!value || !value.trim()) {
          return "Phone number is required";
        }
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ""))) {
          return "Please enter a valid phone number";
        }
        return "";

      case "checkbox":
        if (!value || value.length === 0) {
          return "Please select at least one option";
        }
        return "";

      case "select":
        if (!value) {
          return "Please select an option";
        }
        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    questions.forEach(question => {
      if (question.required) {
        const error = validateField(
          question.name,
          formData[question.name],
          question.type
        );
        if (error) {
          newErrors[question.name] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

// ✅ FIXED - Complete the checkExistingRegistration function
const checkExistingRegistration = async (email) => {
  const foundAccounts = [];

  try {
    // Check 1: Investment/Trading users (UserRegistrations)
    const investmentQuery = query(
      collection(db, "UserRegistrations"), 
      where("email", "==", email)
    );
    const investmentSnapshot = await getDocs(investmentQuery);
    
    if (!investmentSnapshot.empty) {
      foundAccounts.push({
        type: 'Investment Services',
        collection: 'UserRegistrations',
        data: investmentSnapshot.docs[0].data(),
        id: investmentSnapshot.docs[0].id
      });
    }

    // Check 2: Training program users (FormRegistration)
    const trainingQuery = query(
      collection(db, "FormRegistration"), 
      where("emailAddress", "==", email)
    );
    const trainingSnapshot = await getDocs(trainingQuery);
    
    if (!trainingSnapshot.empty) {
      foundAccounts.push({
        type: 'Training Program',
        collection: 'FormRegistration',
        data: trainingSnapshot.docs[0].data(),
        id: trainingSnapshot.docs[0].id
      });
    }

    // Check 3: General investors (Investors)
    const currentUser = auth.currentUser;
    if (currentUser) {
      const investorDoc = doc(db, "Investors", currentUser.uid);
      const investorSnap = await getDoc(investorDoc);
      
      if (investorSnap.exists()) {
        foundAccounts.push({
          type: 'General Investment',
          collection: 'Investors',
          data: investorSnap.data(),
          id: investorSnap.id
        });
      }
    }

    return foundAccounts; // ✅ RETURN the array
  } catch (error) {
    console.error("Error checking existing registration:", error);
    return [];
  }
}; // ✅ CLOSE the function properly

// ✅ MOVE handleNext OUTSIDE and make it a separate function
const handleNext = async () => {
  const currentField = questions[currentQuestion];
  const value = formData[currentField.name];
  
  const error = validateField(currentField.name, value, currentField.type);
  
  if (error) {
    setErrors(prev => ({ ...prev, [currentField.name]: error }));
    return;
  }

  // Skip email checking since it's the authenticated user's email
  if (currentField.name === 'email') {
    // Just move to next question
  }

  setErrors(prev => ({ ...prev, [currentField.name]: '' }));
  setCurrentQuestion(prev => prev + 1);
};

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Don't allow email changes
    if (name === 'email') {
      return;
    }
    
    if (type === "checkbox") {
      const currentValues = formData[name] || [];
      const updatedValues = checked
        ? [...currentValues, value]
        : currentValues.filter(item => item !== value);
      
      setFormData(prev => ({ ...prev, [name]: updatedValues }));
      
      const error = validateField(name, updatedValues, type);
      setErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      const error = validateField(name, value, type);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsRegistering(true);
    setSubmissionStatus("submitting");

    try {
      // Validate form first
      if (!validateForm()) {
        setSubmissionStatus("error");
        setIsRegistering(false);
        return;
      }

      // Check for existing registration in Firestore using authenticated email
     const existingAccounts = await checkExistingRegistration(formData.email);
if (existingAccounts.length > 0) {
  const accountTypes = existingAccounts.map(acc => acc.type).join(', ');
  
  setErrors(prev => ({
    ...prev,
    submit: `You already have accounts for: ${accountTypes}. Please use the dashboard selection to access your services.`
  }));
  setSubmissionStatus("existing_account");
  
  // Show options to user
  setTimeout(() => {
    const goToDashboard = window.confirm(
      `You have existing accounts for: ${accountTypes}\n\nWould you like to go to your dashboard selection?`
    );
    
    if (goToDashboard) {
      navigate('/dashboard-selection');
    }
  }, 2000);
  
  setIsRegistering(false);
  return;
}

      console.log('Starting registration process...');
      console.log('Form data:', formData);

      // Store in Firestore with authenticated user's UID
      const registrationData = {
        ...formData,
        userId: 'U' + Date.now().toString(36),
        firebaseUid: auth.currentUser.uid, // Link to Firebase Auth
        createdAt: serverTimestamp(),
        status: 'pending_payment',
        backendRegistered: false,
        subscriptionStatus: { status: 'pending' },
        lastUpdated: serverTimestamp()
      };

      console.log('Saving to Firestore...');
      const docRef = await addDoc(collection(db, "UserRegistrations"), registrationData);

      // Try to register with backend
      try {
        const registrationResponse = await tradingApi.registerUser({
          email: formData.email,
          accountNumber: formData.accountNumber,
          broker: formData.brokerName,
          lotMultiplier: parseFloat(formData.lotMultiplier),
          subscriptionType: formData.subscriptionPlan
        });

        console.log('Backend registration response:', registrationResponse);

        if (registrationResponse.success) {
          registrationData.userId = registrationResponse.data?.userId || registrationData.userId;
          registrationData.backendRegistered = true;
          registrationData.subscriptionStatus = registrationResponse.data?.subscriptionStatus || { status: 'active' };
        }
      } catch (apiError) {
        console.warn('Backend registration failed, continuing with Firestore only:', apiError);
      }

      localStorage.setItem('registrationId', docRef.id);
      localStorage.setItem('userId', registrationData.userId);
      localStorage.setItem('userEmail', formData.email);
      
      // Trigger email notification to admin
      try {
        await EmailNotificationService.notifyInstitutionalRegistration({
          name: formData.name,
          email: formData.email,
          brokerName: formData.brokerName,
          initialCapital: formData.initialCapital,
          subscriptionPlan: formData.subscriptionPlan
        });
        console.log('Admin email notification sent');
      } catch (emailError) {
        console.error('Failed to send admin email notification:', emailError);
      }

      console.log('Registration successful, redirecting...');
      setSubmissionStatus("success");
      
      // In handleSubmit success section:
setTimeout(() => {
  // Institutional PAMM accounts don't require upfront USDT payment, fees are deducted automatically from profits.
  navigate("/ConfettiCelebration", {
    state: {
      redirectTo: "/investdashboard",
      userType: "investment",
      plan: formData.subscriptionPlan
    }
  });
}, 1000);
      
    } catch (error) {
      console.error("Registration error:", error);
      setSubmissionStatus("error");
      setErrors(prev => ({
        ...prev,
        submit: error.message || "Registration failed. Please try again."
      }));
    } finally {
      setIsRegistering(false);
    }
  };

  const renderField = (question) => {
    const error = errors[question.name];
    
    const inputClasses = `w-full border ${
      error ? 'border-red-400' : 'border-gray-600'
    } px-4 py-3 rounded-xl text-white bg-gray-800/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 placeholder-gray-400`;

    const readOnlyClasses = `w-full border border-gray-500 px-4 py-3 rounded-xl text-gray-300 bg-gray-700/50 backdrop-blur-sm cursor-not-allowed`;

    switch (question.type) {
      case "text":
      case "tel":
        return (
          <div className="space-y-2">
            <input
              type={question.type}
              name={question.name}
              value={formData[question.name]}
              onChange={handleChange}
              className={inputClasses}
              placeholder={question.placeholder || `Enter ${question.text.toLowerCase()}`}
            />
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm flex items-center gap-2"
              >
                <span>⚠️</span> {error}
              </motion.div>
            )}
          </div>
        );

      case "email-readonly":
        return (
          <div className="space-y-2">
            <input
              type="email"
              name={question.name}
              value={formData[question.name]}
              readOnly
              className={readOnlyClasses}
            />
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <span>🔒</span> 
              <span>{question.note}</span>
            </div>
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm flex items-center gap-2"
              >
                <span>⚠️</span> {error}
              </motion.div>
            )}
          </div>
        );

      case "select":
        return (
          <div className="space-y-2">
            <select
              name={question.name}
              value={formData[question.name]}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">Select an option...</option>
              {question.options.map((option) => (
                <option key={option.value} value={option.value} className="bg-gray-800">
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm flex items-center gap-2"
              >
                <span>⚠️</span> {error}
              </motion.div>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {question.options.map((option) => (
                <motion.div 
                  key={option.value} 
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <input
                    type="checkbox"
                    id={option.value}
                    name={question.name}
                    value={option.value}
                    checked={(formData[question.name] || []).includes(option.value)}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <label 
                    htmlFor={option.value} 
                    className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      (formData[question.name] || []).includes(option.value)
                        ? 'border-blue-500 bg-blue-500/20 text-blue-200'
                        : 'border-gray-600 bg-gray-800/30 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                      (formData[question.name] || []).includes(option.value)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-500'
                    }`}>
                      {(formData[question.name] || []).includes(option.value) && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-white text-xs"
                        >
                          ✓
                        </motion.span>
                      )}
                    </div>
                    <span className="font-medium">{option.label}</span>
                  </label>
                </motion.div>
              ))}
            </div>
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-400 text-sm flex items-center gap-2"
              >
                <span>⚠️</span> {error}
              </motion.div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative container mx-auto p-4 flex flex-col items-center justify-center min-h-screen"
      >
        <div className="w-full max-w-2xl bg-black/20 backdrop-blur-md rounded-xl p-4 sm:p-8 border border-white/10 shadow-2xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-2 rounded-full text-white font-semibold mb-4">
              <span className="text-2xl">⚡</span>
              <span>INSTITUTIONAL PORTFOLIOS</span>
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Institutional Portfolios
            </h1>
            <p className="text-gray-300 text-sm sm:text-lg">
              Complete your Institutional Portfolio application
            </p>
          </motion.div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-white font-semibold text-sm sm:text-lg flex items-center gap-2">
                <span className="text-2xl">{questions[currentQuestion].icon}</span>
                Step {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-blue-400 font-bold text-sm sm:text-lg">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}%
              </span>
            </div>
            <div className="relative h-3 bg-gray-700/50 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
            </div>
          </div>

          {/* Question */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="mb-8"
            >
              <h2 className="text-lg sm:text-2xl font-bold mb-6 text-white flex items-center gap-3">
                <span className="text-2xl sm:text-3xl">{questions[currentQuestion].icon}</span>
                {questions[currentQuestion].text}
                {questions[currentQuestion].required && (
                  <span className="text-red-400 ml-1">*</span>
                )}
              </h2>
              {renderField(questions[currentQuestion])}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <motion.div className="flex justify-between items-center mt-8">
            {currentQuestion > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentQuestion(prev => prev - 1)}
                className="px-4 sm:px-6 py-3 text-sm sm:text-base bg-gray-700/50 text-white rounded-xl hover:bg-gray-600/50 transition-all duration-200 backdrop-blur-sm border border-gray-600"
              >
                ← Previous
              </motion.button>
            )}
            
            {currentQuestion < questions.length - 1 ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNext}
                className="px-4 sm:px-8 py-3 text-sm sm:text-base bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 ml-auto font-semibold"
              >
                Next →
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
                disabled={isRegistering}
                className="px-4 sm:px-8 py-3 text-sm sm:text-base bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 ml-auto font-semibold disabled:opacity-50"
              >
                {isRegistering ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Processing...
                  </span>
                ) : (
                  "Complete Registration 🚀"
                )}
              </motion.button>
            )}
          </motion.div>

          {/* Error Message */}
{submissionStatus === "error" && (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-6 text-red-400 text-center bg-red-500/10 border border-red-500/20 p-4 rounded-xl backdrop-blur-sm"
  >
    <span className="text-2xl mr-2">❌</span>
    {errors.submit || "Registration failed. Please try again."}
  </motion.div>
)}

{/* ✅ ADD THIS - Existing Account Message */}
{submissionStatus === "existing_account" && (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="mt-6 text-blue-400 text-center bg-blue-500/10 border border-blue-500/20 p-4 rounded-xl backdrop-blur-sm"
  >
    <span className="text-2xl mr-2">ℹ️</span>
    <div>
      <p className="font-semibold mb-2">Account Already Exists!</p>
      <p className="text-sm mb-4">{errors.submit}</p>
      <div className="flex justify-center space-x-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/dashboard-selection')}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all font-semibold"
        >
          📊 Go to My Accounts
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/auth')}
          className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-all font-semibold"
        >
          🔐 Sign In Instead
        </motion.button>
      </div>
    </div>
  </motion.div>
)}
        </div>
      </motion.div>
    </div>
  );
}

export default InvestRegistration;